import type { AppError } from "../../app.types";
import { sessionTokenFailedExceptionBaseMessage } from "..";

type ShopifyGlobal = {
  idToken: () => Promise<string>;
};

/**
 * Detect whether app is running embedded in Shopify Admin iframe.
 */
export const isEmbedded = (): boolean => {
  try {
    return globalThis.top !== globalThis.self;
  } catch {
    return false;
  }
};

type EmbeddedResult =
  | { isEmbedded: true; config: { apiKey: string } }
  | { isEmbedded: false; config: null };

export const getEmbeddedState = (apiKey?: string): EmbeddedResult => {
  if (isEmbedded() && apiKey) {
    return {
      isEmbedded: true,
      config: { apiKey },
    };
  }

  return {
    isEmbedded: false,
    config: null,
  };
};

const getShopifyGlobal = (): ShopifyGlobal => {
  if (!isEmbedded()) {
    throw new Error("APP_NOT_EMBEDDED");
  }

  const shopifyGlobal = (globalThis as { shopify?: ShopifyGlobal }).shopify;
  if (!shopifyGlobal || typeof shopifyGlobal.idToken !== "function") {
    throw new Error("APP_BRIDGE_UNAVAILABLE");
  }

  return shopifyGlobal;
};

export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs = 4000,
): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => {
        const err = new Error("Session token request timed out");
        err.name = TOKEN_ERRORS.TIMEOUT; // or attach as property
        reject(err);
      }, timeoutMs),
    ),
  ]);

export const TOKEN_ERRORS = {
  TIMEOUT: "TOKEN_TIMEOUT",
  APP_NOT_EMBEDDED: "APP_NOT_EMBEDDED",
  APP_BRIDGE_UNAVAILABLE: "APP_BRIDGE_UNAVAILABLE",
  INVALID_ORIGIN: "INVALID_ORIGIN",
} as const;

export const getErrorCode = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }
  // Handle real Error instances first
  if (error instanceof Error) {
    return error.name || error.message;
  }
  if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>;
    if (typeof err.code === "string") {
      return err.code;
    }
    if (typeof err.message === "string") {
      return err.message;
    }
  }
  return "UNKNOWN_ERROR";
};

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  console.error("Non-Error object caught:", error);
  return "";
}

export const getToken = async (
  useDummyToken = false,
): Promise<string | undefined> => {
  let token;
  if (useDummyToken) {
    token = "dummy-token";
    return token;
  }
  try {
    const shopifyGlobal = getShopifyGlobal();
    token = await withTimeout(shopifyGlobal.idToken(), 2000);
  } catch (error: unknown) {
    const code = getErrorCode(error);
    const message = getErrorMessage(error);

    switch (code) {
      case TOKEN_ERRORS.APP_NOT_EMBEDDED:
        console.warn(
          `${sessionTokenFailedExceptionBaseMessage} App is not embedded`,
        );
        break;
      case TOKEN_ERRORS.TIMEOUT:
        console.warn(
          `${sessionTokenFailedExceptionBaseMessage} Session token request timed out`,
        );
        break;
      case TOKEN_ERRORS.APP_BRIDGE_UNAVAILABLE:
        console.warn(
          `${sessionTokenFailedExceptionBaseMessage} App Bridge is unavailable or not configured`,
        );
        break;
      case TOKEN_ERRORS.INVALID_ORIGIN:
        console.warn(
          `${sessionTokenFailedExceptionBaseMessage} Invalid postMessage origin`,
        );
        break;
      default:
        console.error(
          `${sessionTokenFailedExceptionBaseMessage} Unexpected token error`,
          error,
        );
    }

    if (error instanceof Error) {
      (error as AppError).customMessage =
        `${sessionTokenFailedExceptionBaseMessage} ${message}`;
      throw error;
    }
    const wrappedError = new Error(message) as AppError;
    wrappedError.customMessage = `${sessionTokenFailedExceptionBaseMessage} ${message}`;
    throw wrappedError;
  }

  return token;
};

export const shopifyHeaders = {
  "X-Shopify-Shop-Domain": "dhl-dev-test-tdsmgf3g.myshopify.com",
};
